import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Building2, DollarSign, Save, RefreshCw, Upload, X, Image, Plus, Check, Trash2, Edit2 } from 'lucide-react';
import { extractErrorMessage } from '../../utils/errorHandler';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  
  const [settings, setSettings] = useState({
    id: '',
    name: '',
    is_active: false,
    company_name: '',
    company_address: '',
    company_tel: '',
    company_fax: '',
    company_email: '',
    company_logo: '',
    bank1_name: '',
    bank1_swift: '',
    bank1_account_no: '',
    bank1_account_name: '',
    bank1_address: '',
    bank2_name: '',
    bank2_swift: '',
    bank2_branch: '',
    bank2_address: '',
    invoice_prefix: 'INV-',
    payment_note: '',
    payment_remarks: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId && companies.length > 0) {
      const company = companies.find(c => c.id === selectedCompanyId);
      if (company) {
        setSettings(company);
      }
    }
  }, [selectedCompanyId, companies]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/settings/companies`);
      setCompanies(response.data);
      
      // Select active company by default
      const activeCompany = response.data.find(c => c.is_active);
      if (activeCompany) {
        setSelectedCompanyId(activeCompany.id);
      } else if (response.data.length > 0) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedCompanyId) {
      toast.error('Please select a company');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/companies/${selectedCompanyId}`, settings);
      toast.success('Company settings updated successfully!');
      fetchCompanies();
    } catch (error) {
      console.error('Error saving settings:', error);
      const message = extractErrorMessage(error, 'Failed to update settings');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }

    try {
      const response = await axios.post(`${API}/settings/companies`, {
        name: newCompanyName
      });
      toast.success('Company created successfully!');
      setShowCreateDialog(false);
      setNewCompanyName('');
      fetchCompanies();
      setSelectedCompanyId(response.data.id);
    } catch (error) {
      console.error('Error creating company:', error);
      const message = extractErrorMessage(error, 'Failed to create company');
      toast.error(message);
    }
  };

  const handleActivateCompany = async (companyId) => {
    try {
      await axios.put(`${API}/settings/companies/${companyId}/activate`);
      toast.success('Company activated successfully!');
      fetchCompanies();
    } catch (error) {
      console.error('Error activating company:', error);
      const message = extractErrorMessage(error, 'Failed to activate company');
      toast.error(message);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/settings/companies/${companyId}`);
      toast.success('Company deleted successfully!');
      fetchCompanies();
      setSelectedCompanyId('');
    } catch (error) {
      console.error('Error deleting company:', error);
      const message = extractErrorMessage(error, 'Failed to delete company');
      toast.error(message);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size too large. Maximum size is 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/company/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully!');
      setSettings(prev => ({
        ...prev,
        company_logo: response.data.logo_url
      }));
      
      // Save the logo URL to the selected company
      await axios.put(`${API}/settings/companies/${selectedCompanyId}`, {
        company_logo: response.data.logo_url
      });
      fetchCompanies();
    } catch (error) {
      console.error('Error uploading logo:', error);
      const message = extractErrorMessage(error, 'Failed to upload logo');
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the company logo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/settings/company/logo`);
      toast.success('Logo deleted successfully!');
      setSettings(prev => ({
        ...prev,
        company_logo: ''
      }));
      
      // Update the selected company
      await axios.put(`${API}/settings/companies/${selectedCompanyId}`, {
        company_logo: ''
      });
      fetchCompanies();
    } catch (error) {
      console.error('Error deleting logo:', error);
      const message = extractErrorMessage(error, 'Failed to delete logo');
      toast.error(message);
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    if (logoPath.startsWith('/uploads/')) {
      return `${BACKEND_URL}${logoPath}`;
    }
    return null;
  };

  const logoUrl = getLogoUrl(settings.company_logo);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Selector and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Profiles
              </CardTitle>
              <CardDescription>
                Manage multiple company profiles for invoicing
              </CardDescription>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Company
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Company</DialogTitle>
                  <DialogDescription>
                    Create a new company profile for invoicing
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="e.g., Company 2"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateCompany}>
                      Create Company
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Company Selector */}
            <div>
              <Label>Select Company to Edit</Label>
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      <div className="flex items-center gap-2">
                        {company.name}
                        {company.is_active && (
                          <Badge className="ml-2 bg-green-600">Active</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Company List with Actions */}
            {selectedCompanyId && (
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-slate-400" />
                  <div>
                    <p className="font-medium">{settings.name}</p>
                    <p className="text-sm text-slate-600">{settings.company_name || 'Not configured'}</p>
                  </div>
                  {settings.is_active && (
                    <Badge className="bg-green-600">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!settings.is_active && (
                    <Button
                      size="sm"
                      onClick={() => handleActivateCompany(settings.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Set as Active
                    </Button>
                  )}
                  {!settings.is_active && companies.length > 1 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteCompany(settings.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Company Details Form */}
      {selectedCompanyId && (
        <>
          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Basic information about your company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                />
              </div>

              <div>
                <Label htmlFor="company_address">Company Address</Label>
                <Textarea
                  id="company_address"
                  name="company_address"
                  value={settings.company_address}
                  onChange={handleChange}
                  placeholder="Full company address"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_tel">Telephone</Label>
                  <Input
                    id="company_tel"
                    name="company_tel"
                    value={settings.company_tel}
                    onChange={handleChange}
                    placeholder="+81-XXX-XXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="company_fax">Fax</Label>
                  <Input
                    id="company_fax"
                    name="company_fax"
                    value={settings.company_fax}
                    onChange={handleChange}
                    placeholder="+81-XXX-XXXX"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company_email">Email</Label>
                <Input
                  id="company_email"
                  name="company_email"
                  type="email"
                  value={settings.company_email}
                  onChange={handleChange}
                  placeholder="info@company.com"
                />
              </div>

              {/* Company Logo */}
              <div>
                <Label>Company Logo</Label>
                <div className="mt-2 space-y-4">
                  {logoUrl ? (
                    <div className="relative inline-block">
                      <img
                        src={logoUrl}
                        alt="Company Logo"
                        className="h-24 w-auto border-2 border-slate-200 rounded-lg"
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2"
                        onClick={handleLogoDelete}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <Image className="h-12 w-12 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600 mb-2">No logo uploaded</p>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="logo-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingLogo}
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        {uploadingLogo ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {logoUrl ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                    </label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      JPEG, PNG, GIF or WebP (Max 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Bank Information
              </CardTitle>
              <CardDescription>Primary and intermediary bank details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary Bank */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Primary Bank</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="bank1_name">Bank Name</Label>
                    <Input
                      id="bank1_name"
                      name="bank1_name"
                      value={settings.bank1_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank1_swift">SWIFT Code</Label>
                    <Input
                      id="bank1_swift"
                      name="bank1_swift"
                      value={settings.bank1_swift}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank1_account_no">Account Number</Label>
                    <Input
                      id="bank1_account_no"
                      name="bank1_account_no"
                      value={settings.bank1_account_no}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="bank1_account_name">Account Name</Label>
                    <Input
                      id="bank1_account_name"
                      name="bank1_account_name"
                      value={settings.bank1_account_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="bank1_address">Bank Address</Label>
                    <Input
                      id="bank1_address"
                      name="bank1_address"
                      value={settings.bank1_address}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Intermediary Bank */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Intermediary Bank</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="bank2_name">Bank Name</Label>
                    <Input
                      id="bank2_name"
                      name="bank2_name"
                      value={settings.bank2_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank2_swift">SWIFT Code</Label>
                    <Input
                      id="bank2_swift"
                      name="bank2_swift"
                      value={settings.bank2_swift}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank2_branch">Branch</Label>
                    <Input
                      id="bank2_branch"
                      name="bank2_branch"
                      value={settings.bank2_branch}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="bank2_address">Bank Address</Label>
                    <Input
                      id="bank2_address"
                      name="bank2_address"
                      value={settings.bank2_address}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Settings</CardTitle>
              <CardDescription>Configure invoice numbering and payment terms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
                <Input
                  id="invoice_prefix"
                  name="invoice_prefix"
                  value={settings.invoice_prefix}
                  onChange={handleChange}
                  placeholder="INV-"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Example: INV-001, INV-002, etc.
                </p>
              </div>

              <div>
                <Label htmlFor="payment_note">Payment Note</Label>
                <Textarea
                  id="payment_note"
                  name="payment_note"
                  value={settings.payment_note}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Bank charges note"
                />
              </div>

              <div>
                <Label htmlFor="payment_remarks">Payment Remarks</Label>
                <Textarea
                  id="payment_remarks"
                  name="payment_remarks"
                  value={settings.payment_remarks}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Payment instructions"
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySettings;
